import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  FormControlLabel,
  GlobalStyles,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDownward,
  ArrowUpward,
  Cancel,
  CheckCircleOutline,
  Print,
} from '@mui/icons-material';
import { useFirebaseContext } from '../lib/firebase-context';
import {
  AttendanceRow,
  GroupAttendanceSection,
  buildAttendanceRows,
  buildGroupAttendanceSections,
  selectQualifyingDates,
} from '../lib/attendance';
import { formatGroupShort } from '../lib/groups';
import { CheckinDate } from '../lib/types';

// Single-purpose page that renders a per-attendee, per-date attendance
// matrix for the active event. Read-only — no audited writes, no s3
// invalidation, no activity-log entries. All data comes from
// `useFirebaseContext()`; no new Firestore listeners.
//
// See docs/superpowers/specs/2026-06-07-attendance-report-design.md.

const INCLUDE_TODAY_KEY = 'attendanceIncludeToday';
const PRINT_TARGET_ID = 'attendance-print-target';

const readPersistedBool = (key: string): boolean =>
  localStorage.getItem(key) === '1';

const writePersistedBool = (key: string, value: boolean): void => {
  localStorage.setItem(key, value ? '1' : '0');
};

type SortKey =
  | { kind: 'missed' }
  | { kind: 'name'; dir: 'asc' | 'desc' }
  | { kind: 'date'; dateRef: string };

const SHORT_DATE_OPTS: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  month: 'numeric',
  day: 'numeric',
};

const LONG_DATE_OPTS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: 'long',
  day: 'numeric',
  weekday: 'long',
};

const formatShort = (d: CheckinDate): string =>
  d.date.toDate().toLocaleDateString('en-US', SHORT_DATE_OPTS);

const formatLong = (d: CheckinDate): string =>
  d.date.toDate().toLocaleDateString('en-US', LONG_DATE_OPTS);

// Print styles mirror the Roster's pattern in `groups-roster.tsx`, scoped
// to the attendance print target. Landscape because the matrix grows
// wide with many dates.
const printStyles = (
  <GlobalStyles
    styles={{
      '@media print': {
        '@page': { size: 'letter landscape', margin: '0.25in' },
        '.MuiAppBar-root': { display: 'none !important' },
        'footer, [role="contentinfo"]': { display: 'none !important' },
        '.MuiSnackbar-root, .MuiBackdrop-root, .MuiDialog-root': {
          display: 'none !important',
        },
        '.attendance-print-page-header': {
          display: 'none !important',
        },
        body: { backgroundColor: 'white !important' },
        [`#${PRINT_TARGET_ID}, #${PRINT_TARGET_ID} *`]: {
          color: 'black !important',
        },
        [`#${PRINT_TARGET_ID} .MuiPaper-root, #${PRINT_TARGET_ID} .MuiCard-root`]:
          {
            backgroundColor: 'white !important',
            backgroundImage: 'none !important',
            boxShadow: 'none !important',
            border: 'none !important',
            margin: '0 !important',
          },
        [`#${PRINT_TARGET_ID} .MuiCardContent-root`]: {
          padding: '0 !important',
        },
        [`#${PRINT_TARGET_ID} .MuiTableCell-root`]: {
          borderColor: 'black !important',
          padding: '4px 6px !important',
        },
        [`#${PRINT_TARGET_ID} .MuiChip-root`]: {
          backgroundColor: 'transparent !important',
          border: '1px solid black !important',
        },
        // "By group" tab: each class prints on its own sheet, mirroring
        // the Roster PDF. `:last-child` prevents a blank trailing page.
        [`#${PRINT_TARGET_ID} .attendance-group-card`]: {
          pageBreakAfter: 'always',
        },
        [`#${PRINT_TARGET_ID} .attendance-group-card:last-child`]: {
          pageBreakAfter: 'auto',
        },
      },
    }}
  />
);

const AttendancePage = () => {
  const { activeEvent, attendees, checkInDates, groups } = useFirebaseContext();

  const [includeToday, setIncludeToday] = useState(() =>
    readPersistedBool(INCLUDE_TODAY_KEY),
  );
  const [sortKey, setSortKey] = useState<SortKey>({ kind: 'missed' });
  const [tab, setTab] = useState<'missed' | 'groups'>('missed');

  const handleIncludeTodayChange = (next: boolean) => {
    setIncludeToday(next);
    writePersistedBool(INCLUDE_TODAY_KEY, next);
  };

  // Match `localDateKey` (lib/dates.ts) — `toLocaleDateString('en-CA')`
  // produces 'yyyy-mm-dd', which sorts lexicographically in ISO order.
  const todayKey = useMemo(
    () => new Date().toLocaleDateString('en-CA'),
    [],
  );

  // Live attendee refs for the soft-delete filter inside
  // selectQualifyingDates — same pattern used by `check-in-date.tsx` and
  // `check-in-dates-list.utils.tsx` to ignore stale refs left behind by
  // soft-deleted attendees.
  const liveAttendeeRefs = useMemo(
    () => new Set(attendees.map((a) => a.ref)),
    [attendees],
  );

  const qualifyingDates = useMemo(
    () =>
      selectQualifyingDates(
        checkInDates,
        liveAttendeeRefs,
        todayKey,
        includeToday,
      ),
    [checkInDates, liveAttendeeRefs, todayKey, includeToday],
  );

  const rows = useMemo(
    () => buildAttendanceRows(attendees, qualifyingDates),
    [attendees, qualifyingDates],
  );

  const groupSections = useMemo(
    () => buildGroupAttendanceSections(attendees, groups, qualifyingDates),
    [attendees, groups, qualifyingDates],
  );

  // The default-sorted list comes straight from `buildAttendanceRows`
  // (missed desc, name asc). Other sort modes are derived here so the
  // memoised `rows` doesn't churn when only `sortKey` changes.
  const sortedRows = useMemo(() => {
    if (sortKey.kind === 'missed') return rows;
    if (sortKey.kind === 'name') {
      const dir = sortKey.dir === 'asc' ? 1 : -1;
      return [...rows].sort((a, b) => {
        const lastDiff = (a.attendee.last_name ?? '').localeCompare(
          b.attendee.last_name ?? '',
          undefined,
          { sensitivity: 'base' },
        );
        if (lastDiff !== 0) return lastDiff * dir;
        const firstDiff = (a.attendee.first_name ?? '').localeCompare(
          b.attendee.first_name ?? '',
          undefined,
          { sensitivity: 'base' },
        );
        return firstDiff * dir;
      });
    }
    const idx = qualifyingDates.findIndex((d) => d.ref === sortKey.dateRef);
    if (idx === -1) return rows;
    return [...rows].sort((a, b) => {
      const aMissed = !a.presence[idx];
      const bMissed = !b.presence[idx];
      if (aMissed !== bMissed) return aMissed ? -1 : 1;
      if (a.missedCount !== b.missedCount) {
        return b.missedCount - a.missedCount;
      }
      return (a.attendee.last_name ?? '').localeCompare(
        b.attendee.last_name ?? '',
        undefined,
        { sensitivity: 'base' },
      );
    });
  }, [rows, sortKey, qualifyingDates]);

  const groupLabelByRef = useMemo(() => {
    const map = new Map<string, string>();
    for (const g of groups) map.set(g.ref, formatGroupShort(g));
    return map;
  }, [groups]);

  const renderHeader = () => (
    <Box
      className='attendance-print-page-header'
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2,
        gap: 2,
        flexWrap: 'wrap',
      }}
    >
      <Typography variant='h5'>Attendance</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={includeToday}
              onChange={(e) => handleIncludeTodayChange(e.target.checked)}
            />
          }
          label='Include today'
        />
        <Button
          variant='outlined'
          startIcon={<Print />}
          onClick={() => window.print()}
        >
          Print
        </Button>
      </Box>
    </Box>
  );

  if (!activeEvent) {
    return (
      <>
        {printStyles}
        {renderHeader()}
        <Alert severity='warning'>
          No event is active. Ask an admin to set one.
        </Alert>
      </>
    );
  }

  if (qualifyingDates.length === 0) {
    return (
      <>
        {printStyles}
        {renderHeader()}
        <Alert severity='info'>Event hasn&apos;t started yet.</Alert>
      </>
    );
  }

  const renderSortGlyph = (kind: SortKey['kind'], dateRef?: string) => {
    if (sortKey.kind !== kind) return null;
    if (sortKey.kind === 'date' && dateRef !== undefined) {
      if (sortKey.dateRef !== dateRef) return null;
    }
    if (sortKey.kind === 'name') {
      return sortKey.dir === 'asc' ? (
        <ArrowUpward
          fontSize='inherit'
          sx={{ ml: 0.5, fontSize: '0.9em', verticalAlign: 'middle' }}
        />
      ) : (
        <ArrowDownward
          fontSize='inherit'
          sx={{ ml: 0.5, fontSize: '0.9em', verticalAlign: 'middle' }}
        />
      );
    }
    return (
      <ArrowDownward
        fontSize='inherit'
        sx={{ ml: 0.5, fontSize: '0.9em', verticalAlign: 'middle' }}
      />
    );
  };

  const handleNameClick = () => {
    setSortKey((prev) => {
      if (prev.kind !== 'name') return { kind: 'name', dir: 'asc' };
      return { kind: 'name', dir: prev.dir === 'asc' ? 'desc' : 'asc' };
    });
  };

  const headerCellSx = {
    cursor: 'pointer',
    userSelect: 'none' as const,
    whiteSpace: 'nowrap' as const,
  };

  const rowCount = sortedRows.length;
  const subheading = `Showing ${rowCount} ${
    rowCount === 1 ? 'attendee who has' : 'attendees who have'
  } missed at least one day.`;

  const missedTab =
    sortedRows.length === 0 ? (
      <Alert severity='success'>Everyone&apos;s been here every day.</Alert>
    ) : (
      <>
        <Typography
          variant='body2'
          color='text.secondary'
          sx={{ mb: 2 }}
        >
          {subheading}
        </Typography>
        <Card>
          <CardContent>
            <TableContainer>
              <Table
                size='small'
                sx={{
                  '& tbody tr:last-child td': { borderBottom: 0 },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell sx={headerCellSx} onClick={handleNameClick}>
                      Name{renderSortGlyph('name')}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>Group</TableCell>
                    {qualifyingDates.map((d) => (
                      <TableCell
                        key={d.ref}
                        align='center'
                        sx={headerCellSx}
                        onClick={() =>
                          setSortKey({ kind: 'date', dateRef: d.ref })
                        }
                      >
                        <Tooltip title={formatLong(d)}>
                          <span>
                            {formatShort(d)}
                            {renderSortGlyph('date', d.ref)}
                          </span>
                        </Tooltip>
                      </TableCell>
                    ))}
                    <TableCell
                      align='right'
                      sx={headerCellSx}
                      onClick={() => setSortKey({ kind: 'missed' })}
                    >
                      Missed{renderSortGlyph('missed')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedRows.map((row) => (
                    <AttendanceMatrixRow
                      key={row.attendee.ref}
                      row={row}
                      qualifyingDates={qualifyingDates}
                      groupLabelByRef={groupLabelByRef}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </>
    );

  const groupsTab =
    groupSections.length === 0 ? (
      <Alert severity='info'>No attendees yet.</Alert>
    ) : (
      groupSections.map((section) => (
        <GroupAttendanceCard
          key={section.group?.ref ?? 'unassigned'}
          section={section}
          qualifyingDates={qualifyingDates}
        />
      ))
    );

  return (
    <>
      {printStyles}
      {renderHeader()}
      <Tabs
        value={tab}
        onChange={(_event, value: 'missed' | 'groups') => setTab(value)}
        className='attendance-print-page-header'
        sx={{ mb: 2 }}
      >
        <Tab label='Missed days' value='missed' />
        <Tab label='By group' value='groups' />
      </Tabs>
      <Box id={PRINT_TARGET_ID}>
        {tab === 'missed' ? missedTab : groupsTab}
      </Box>
    </>
  );
};

// One class's full attendance matrix on the "By group" tab: every kid in
// the group (zero-miss kids included), one ✓/✗ column per qualifying
// date. `group: null` renders the trailing Unassigned bucket.
const GroupAttendanceCard = ({
  section,
  qualifyingDates,
}: {
  section: GroupAttendanceSection;
  qualifyingDates: CheckinDate[];
}) => (
  <Card className='attendance-group-card' sx={{ mb: 2 }}>
    <CardContent>
      <Typography variant='h6' sx={{ mb: 1 }}>
        {section.group ? formatGroupShort(section.group) : 'Unassigned'}
      </Typography>
      <TableContainer>
        <Table
          size='small'
          sx={{
            '& tbody tr:last-child td': { borderBottom: 0 },
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: 'nowrap' }}>Name</TableCell>
              {qualifyingDates.map((d) => (
                <TableCell
                  key={d.ref}
                  align='center'
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  <Tooltip title={formatLong(d)}>
                    <span>{formatShort(d)}</span>
                  </Tooltip>
                </TableCell>
              ))}
              <TableCell align='right'>Missed</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {section.rows.map((row) => (
              <AttendanceMatrixRow
                key={row.attendee.ref}
                row={row}
                qualifyingDates={qualifyingDates}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </CardContent>
  </Card>
);

// Shared by both tabs. `groupLabelByRef` is provided only by the "Missed
// days" tab — the "By group" cards omit the redundant Group column.
const AttendanceMatrixRow = ({
  row,
  qualifyingDates,
  groupLabelByRef,
}: {
  row: AttendanceRow;
  qualifyingDates: CheckinDate[];
  groupLabelByRef?: Map<string, string>;
}) => {
  const groupLabel = row.attendee.groupRef
    ? groupLabelByRef?.get(row.attendee.groupRef) ?? '—'
    : '—';
  const chipColor =
    row.missedCount >= 2
      ? 'error'
      : row.missedCount === 1
        ? 'warning'
        : 'success';

  return (
    <TableRow hover>
      <TableCell sx={{ whiteSpace: 'nowrap' }}>
        {row.attendee.last_name}, {row.attendee.first_name}
      </TableCell>
      {groupLabelByRef && (
        <TableCell sx={{ whiteSpace: 'nowrap' }}>{groupLabel}</TableCell>
      )}
      {row.presence.map((present, i) => (
        <TableCell key={qualifyingDates[i].ref} align='center'>
          <Tooltip title={formatLong(qualifyingDates[i])}>
            {present ? (
              <CheckCircleOutline fontSize='small' color='success' />
            ) : (
              <Cancel fontSize='small' color='error' />
            )}
          </Tooltip>
        </TableCell>
      ))}
      <TableCell align='right'>
        <Chip
          label={row.missedCount}
          color={chipColor}
          size='small'
          sx={{ minWidth: 36 }}
        />
      </TableCell>
    </TableRow>
  );
};

export default AttendancePage;
